/**
 * LeoKit swap client — quote + deposit, MAYAChain only.
 *
 * Replaces the direct mayanode quote in `maya-client.ts` with LeoKit's
 * aggregator endpoints. The aggregator returns offers from every
 * registered protocol (THORChain, MAYAChain, ChainFlip, Relay, Rango,
 * Across, …); we filter strictly to `protocol === "mayachain"` because
 * the wallet's shielded-memo deposit flow only works against MAYA's
 * inbound vault contract.
 *
 * Flow:
 *   1. getLeokitMayaQuote(...) — GET /quote, pull the mayachain entry.
 *   2. getLeokitMayaDeposit(quote_id) — POST /deposit with selected_protocol,
 *      returns the t-vault deposit address + memo string.
 *   3. Wallet broadcasts the shielded send with:
 *        recipient = deposit_address     (the t-vault principal)
 *        memo_note = { recipient_ua = ZEC_MEMO_UA,  memo_text = details.memo }
 *
 * The legacy `getZecMemoUA()` in maya-client.ts is still used to source
 * the memo-recipient UA — LeoKit's deposit response doesn't carry it
 * because non-shielded protocols don't need one.
 */

import { LEOKIT_BASE } from "./leokit-base";

export interface LeokitQuoteFee {
  type: string;
  asset: string;
  /** Already-decimalised amount in target-asset units. */
  amount: number;
  usd: number;
}

export interface LeokitMayaQuote {
  /** Outer wrapper. Identifies the protocol that produced this offer. */
  protocol: "mayachain";
  /** quote_id from the LeoKit envelope — needed for the deposit call. */
  quote_id: string;
  /** Expected destination amount, RAW atomic units of the target asset. */
  expected_amount_out: string;
  /** Decimal places of the target asset (for client-side formatting). */
  out_asset_decimal: number;
  /** USD value of the expected output at quote time. */
  expected_amount_out_usd: number;
  /** USD value of what the user is sending in. */
  input_amount_usd: number;
  /** Per-leg fees as returned by MAYA — already-decimalised. */
  fees: LeokitQuoteFee[];
  /** Sum of every fee's USD value, convenience for the UI. */
  total_fees_usd: number;
  /** Estimated total seconds end-to-end. */
  total_swap_seconds: number;
  /** When this quote stops being honoured by /deposit. */
  expires_at_ms: number;
  ttl_seconds: number;
  /** Free-text route description, e.g. `["ZEC.ZEC", "ETH.ETH"]`. */
  route: string[];
  /** Raw response (for diagnostics in the UI's <details>). */
  raw: Record<string, unknown>;
}

interface LeokitQuoteResponse {
  quote_id?: string;
  quotes?: Array<{
    protocol?: string;
    data?: {
      expected_amount_out?: number | string;
      expected_amount_out_usd?: number;
      input_amount_usd?: number;
      out_asset_decimal?: number;
      in_asset_decimal?: number;
      total_swap_seconds?: number;
      expires_at?: number;
      ttl_seconds?: number;
      route?: string[];
      fees?: LeokitQuoteFee[];
      [k: string]: unknown;
    };
    expectedAmountOutNum?: number;
    totalFeesUsd?: number;
    totalSwapSeconds?: number;
    flags?: string[];
    [k: string]: unknown;
  }>;
  timestamp?: string;
  [k: string]: unknown;
}

export interface LeokitMayaQuoteInput {
  /**
   * HUMAN-unit decimal string of the source asset, e.g. `"0.5"` for half
   * a ZEC or `"100"` for 100 USDC. NOT atomic units — LeoKit's `/quote`
   * endpoint divides nothing on its end; passing `"100000000"` for "1 ZEC"
   * is interpreted as 100,000,000 ZEC and returns absurd output amounts.
   */
  amount: string;
  /** LeoKit asset id, e.g. "ZEC.ZEC". */
  from_asset: string;
  /** LeoKit asset id, e.g. "ETH.ETH" or "ARB.USDC-0x…". */
  to_asset: string;
  /** Wallet sender address (origin). For ZEC use a transparent or unified address. */
  origin: string;
  /** Recipient address on the destination chain. */
  destination: string;
  /** Optional slippage tolerance in basis points (1 = 0.01%). */
  slippage_bps?: number;
  /** Optional affiliate handle (default "leo"). */
  affiliate?: string;
  /** Optional affiliate fee bps (default 45). */
  affiliate_bps?: number;
}

/**
 * Fetch a MAYAChain quote via LeoKit. Throws if no MAYA offer comes
 * back. Used for the ZEC origin path — MAYAChain is the only protocol
 * that supports the shielded-memo deposit flow we need to keep the
 * outgoing tx private.
 */
export async function getLeokitMayaQuote(
  input: LeokitMayaQuoteInput,
  signal?: AbortSignal,
): Promise<LeokitMayaQuote> {
  const qs = new URLSearchParams({
    from_asset: input.from_asset,
    to_asset: input.to_asset,
    amount: input.amount,
    origin: input.origin,
    destination: input.destination,
  });
  if (input.slippage_bps != null) {
    qs.set("slippage_bps", String(input.slippage_bps));
  }
  if (input.affiliate) qs.set("affiliate", input.affiliate);
  if (input.affiliate_bps != null) {
    qs.set("affiliate_bps", String(input.affiliate_bps));
  }
  // LeoKit's `/quote` endpoint races every protocol in parallel and
  // returns whichever responded within the deadline. The mayachain
  // entry is the one we care about; the rest are ignored.
  const url = `${LEOKIT_BASE}/quote?${qs}`;
  const r = await fetch(url, { signal });
  if (!r.ok) {
    let body = "";
    try {
      body = await r.text();
    } catch {
      /* ignore */
    }
    throw new LeokitSwapError(
      `LeoKit /quote → HTTP ${r.status}`,
      r.status,
      body,
    );
  }
  const json = (await r.json()) as LeokitQuoteResponse;
  if (!json.quote_id) {
    throw new LeokitSwapError("LeoKit response missing quote_id", 500);
  }
  const maya = (json.quotes ?? []).find((q) => q.protocol === "mayachain");
  if (!maya || !maya.data) {
    throw new LeokitSwapError(
      "No MAYAChain offer for this pair. Try a smaller amount or a different destination asset.",
      404,
    );
  }
  const fees = maya.data.fees ?? [];
  return {
    protocol: "mayachain",
    quote_id: json.quote_id,
    expected_amount_out: String(maya.data.expected_amount_out ?? "0"),
    out_asset_decimal: Number(maya.data.out_asset_decimal ?? 8),
    expected_amount_out_usd: Number(maya.data.expected_amount_out_usd ?? 0),
    input_amount_usd: Number(maya.data.input_amount_usd ?? 0),
    fees,
    total_fees_usd:
      typeof maya.totalFeesUsd === "number"
        ? maya.totalFeesUsd
        : fees.reduce((s, f) => s + (f.usd ?? 0), 0),
    total_swap_seconds: Number(maya.data.total_swap_seconds ?? 0),
    expires_at_ms: Number(maya.data.expires_at ?? 0),
    ttl_seconds: Number(maya.data.ttl_seconds ?? 0),
    route: Array.isArray(maya.data.route) ? maya.data.route : [],
    raw: maya as Record<string, unknown>,
  };
}

/**
 * Generic best-offer quote. Picks the entry flagged OPTIMAL if any;
 * otherwise the first entry. Use this for non-ZEC origins where the
 * shielded-memo restriction doesn't apply.
 */
export async function getLeokitBestQuote(
  input: LeokitMayaQuoteInput,
  signal?: AbortSignal,
): Promise<Omit<LeokitMayaQuote, "protocol"> & { protocol: string }> {
  const qs = new URLSearchParams({
    from_asset: input.from_asset,
    to_asset: input.to_asset,
    amount: input.amount,
    origin: input.origin,
    destination: input.destination,
  });
  if (input.slippage_bps != null) qs.set("slippage_bps", String(input.slippage_bps));
  if (input.affiliate) qs.set("affiliate", input.affiliate);
  if (input.affiliate_bps != null) qs.set("affiliate_bps", String(input.affiliate_bps));
  const url = `${LEOKIT_BASE}/quote?${qs}`;
  const r = await fetch(url, { signal });
  if (!r.ok) {
    let body = "";
    try {
      body = await r.text();
    } catch {
      /* ignore */
    }
    throw new LeokitSwapError(`LeoKit /quote → HTTP ${r.status}`, r.status, body);
  }
  const json = (await r.json()) as LeokitQuoteResponse;
  if (!json.quote_id) {
    throw new LeokitSwapError("LeoKit response missing quote_id", 500);
  }
  const quotes = json.quotes ?? [];
  if (quotes.length === 0) {
    throw new LeokitSwapError(
      "No protocol could route this swap. Try a smaller amount or a different asset pair.",
      404,
    );
  }
  const pick =
    quotes.find((q) => (q.flags ?? []).includes("OPTIMAL")) ?? quotes[0]!;
  const d = pick.data ?? {};
  const fees = (d.fees ?? []) as LeokitQuoteFee[];
  return {
    protocol: String(pick.protocol ?? "unknown"),
    quote_id: json.quote_id,
    expected_amount_out: String(d.expected_amount_out ?? "0"),
    out_asset_decimal: Number(d.out_asset_decimal ?? 8),
    expected_amount_out_usd: Number(d.expected_amount_out_usd ?? 0),
    input_amount_usd: Number(d.input_amount_usd ?? 0),
    fees,
    total_fees_usd:
      typeof pick.totalFeesUsd === "number"
        ? pick.totalFeesUsd
        : fees.reduce((s, f) => s + (f.usd ?? 0), 0),
    total_swap_seconds: Number(d.total_swap_seconds ?? 0),
    expires_at_ms: Number(d.expires_at ?? 0),
    ttl_seconds: Number(d.ttl_seconds ?? 0),
    route: Array.isArray(d.route) ? (d.route as string[]) : [],
    raw: pick as Record<string, unknown>,
  };
}

// ─── Deposit ────────────────────────────────────────────────────────────

export interface LeokitMayaDeposit {
  /** Echo of the quote_id we sent in. */
  quote_id: string;
  protocol: "mayachain";
  /** "UTXO" | "EVM" | "COSMOS" | ... — informational. */
  type: string;
  /**
   * The t-address vault. For ZEC swaps this is where the SHIELDED tx's
   * principal must land; the memo-note goes to ZEC_MEMO_UA (sourced
   * separately via maya-client.getZecMemoUA).
   */
  deposit_address: string;
  /**
   * The MAYA "=:ASSET:DEST:limit:affiliate:bps" memo string. We use
   * this as the memo-note's `memo_text` for the shielded-memo flow.
   */
  memo: string;
  /** ZIP-321 URI (zcash:t1…?amount=…&memo=base64url). */
  payment_uri: string | null;
  /** CDN-rendered QR PNG, useful for sharing. */
  qr_url: string | null;
  details: Record<string, unknown>;
  raw: Record<string, unknown>;
}

interface LeokitDepositResponse {
  quote_id?: string;
  protocol?: string;
  type?: string;
  deposit_address?: string;
  payment_uri?: string | null;
  qr_url?: string | null;
  details?: { memo?: string; [k: string]: unknown };
  unsigned_transaction?: { deposit_address?: string };
  [k: string]: unknown;
}

/** Same response struct, but with the chosen protocol echoed back. */
export interface LeokitDeposit extends Omit<LeokitMayaDeposit, "protocol"> {
  protocol: string;
  /**
   * For EVM types: unsigned `viem`-compatible TransactionRequest fields.
   * Present only for `type === "EVM"`; UTXO deposits use deposit_address+memo.
   */
  evm_tx?: {
    to: `0x${string}`;
    data?: `0x${string}`;
    value?: string;
    gas?: string;
    chainId: number;
  };
}

/**
 * Bind a quote to a deposit. For UTXO (ZEC/BTC/LTC) protocols, returns
 * the inbound vault `deposit_address` + memo. For EVM, returns an
 * `evm_tx` compatible with viem's `sendTransaction`.
 */
export async function getLeokitDeposit(
  quoteId: string,
  selectedProtocol: string,
  signal?: AbortSignal,
): Promise<LeokitDeposit> {
  const r = await fetch(`${LEOKIT_BASE}/deposit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      quote_id: quoteId,
      selected_protocol: selectedProtocol,
    }),
    signal,
  });
  if (!r.ok) {
    let body = "";
    try {
      body = await r.text();
    } catch {
      /* ignore */
    }
    throw new LeokitSwapError(
      `LeoKit /deposit → HTTP ${r.status}`,
      r.status,
      body,
    );
  }
  const json = (await r.json()) as LeokitDepositResponse;
  const type = String(json.type ?? "UTXO");
  // EVM types carry a viem-shaped TransactionRequest under
  // `unsigned_transaction`; UTXO/COSMOS protocols carry a deposit
  // address + memo string. We pluck both shapes defensively.
  const evmTxRaw = (json.unsigned_transaction ?? {}) as Record<string, unknown>;
  const evm_tx =
    type === "EVM" && typeof evmTxRaw.to === "string"
      ? {
          to: evmTxRaw.to as `0x${string}`,
          data:
            typeof evmTxRaw.data === "string"
              ? (evmTxRaw.data as `0x${string}`)
              : undefined,
          value:
            typeof evmTxRaw.value === "string"
              ? evmTxRaw.value
              : typeof evmTxRaw.value === "number"
                ? String(evmTxRaw.value)
                : undefined,
          gas:
            typeof evmTxRaw.gas === "string"
              ? evmTxRaw.gas
              : typeof evmTxRaw.gas === "number"
                ? String(evmTxRaw.gas)
                : undefined,
          chainId: Number(
            (evmTxRaw.chainId as number | string | undefined) ?? 1,
          ),
        }
      : undefined;
  const deposit_address =
    json.deposit_address ??
    (typeof evmTxRaw.deposit_address === "string"
      ? (evmTxRaw.deposit_address as string)
      : "");
  const memo = json.details?.memo ?? "";
  // UTXO requires both; EVM requires the prepared tx.
  if (type !== "EVM") {
    if (!deposit_address) {
      throw new LeokitSwapError("LeoKit /deposit missing deposit_address", 500);
    }
    if (!memo) {
      throw new LeokitSwapError("LeoKit /deposit missing memo", 500);
    }
  } else if (!evm_tx) {
    throw new LeokitSwapError(
      "LeoKit /deposit missing unsigned EVM transaction",
      500,
    );
  }
  return {
    quote_id: quoteId,
    protocol: selectedProtocol,
    type,
    deposit_address,
    memo,
    payment_uri: json.payment_uri ?? null,
    qr_url: json.qr_url ?? null,
    details: (json.details ?? {}) as Record<string, unknown>,
    evm_tx,
    raw: json as Record<string, unknown>,
  };
}

/** Backwards-compat wrapper used by the ZEC origin flow. */
export async function getLeokitMayaDeposit(
  quoteId: string,
  signal?: AbortSignal,
): Promise<LeokitMayaDeposit> {
  const d = await getLeokitDeposit(quoteId, "mayachain", signal);
  return d as LeokitMayaDeposit;
}

export class LeokitSwapError extends Error {
  public readonly status: number;
  public readonly body: string | null;
  constructor(message: string, status: number, body?: string) {
    super(message);
    this.name = "LeokitSwapError";
    this.status = status;
    this.body = body ?? null;
  }
}
